/**
 * Слияние дублей категорий (§9.1): «холодильники» → «Холодильники».
 *
 * Операция необратима и переносит привязки у живых брендов, поэтому окно
 * сначала проговаривает последствие числом, а кнопка подтверждения появляется
 * только после выбора цели. Esc и клик по подложке окно не закрывают —
 * выход только явной кнопкой (§2.3 про необратимые действия).
 */
import { useEffect, useMemo, useState } from 'react'
import type { Category } from '@shared/types.ts'
import { Button, ConsequenceLine, Modal, Select, type SelectOption } from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { countOf, forms } from '../../texts.js'
import { adminTexts } from '../admin/texts.admin.js'
import { errorText } from '../admin/admin-api.js'
import './MergeCategoriesDialog.css'

const t = adminTexts.merge

export interface MergeCategoriesDialogProps {
  /** Категория-источник: окно открыто, пока она задана. */
  source: Category | null
  /** Весь справочник: в цель годятся только активные и не источник. */
  categories: Category[]
  onClose: () => void
  onMerged: (source: Category, target: Category) => void | Promise<void>
}

export function MergeCategoriesDialog({
  source, categories, onClose, onMerged,
}: MergeCategoriesDialogProps) {
  const toast = useApp((s) => s.toast)
  const [targetId, setTargetId] = useState<string | null>(null)
  const [listOpen, setListOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  // Новый источник — новая цель: прошлый выбор к этой паре не относится.
  useEffect(() => {
    setTargetId(null)
    setListOpen(false)
  }, [source?.id])

  const options = useMemo<SelectOption[]>(() => {
    if (!source) return []
    return categories
      .filter((c) => c.status === 'active' && c.id !== source.id)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      .map((c) => ({
        value: String(c.id),
        label: c.name,
        badge: <span className="merge__count t-meta">{c.usageCount ?? 0}</span>,
      }))
  }, [categories, source])

  const target = useMemo(
    () => categories.find((c) => String(c.id) === targetId) ?? null,
    [categories, targetId],
  )

  async function merge() {
    if (!source || !target) return
    setBusy(true)
    try {
      await api.mergeCategories(source.id, target.id)
      await onMerged(source, target)
    } catch (err) {
      toast('error', errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const sourceBrands = source?.usageCount ?? 0

  return (
    <Modal
      open={source !== null}
      onClose={onClose}
      title={t.title}
      size="m"
      dismissible={false}
      footerNote={t.note}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{adminTexts.common.cancel}</Button>
          <Button
            variant="primary"
            disabled={target === null}
            loading={busy}
            onClick={() => { void merge() }}
          >
            {busy ? t.submitting : t.submit}
          </Button>
        </>
      }
    >
      {source ? (
        <div className="merge">
          <div className="merge__row">
            <div className="merge__side">
              <span className="merge__label t-label">{t.from}</span>
              <p className="merge__source t-body">{t.sourceSummary(source.name, sourceBrands)}</p>
            </div>
            <span className="merge__arrow" aria-hidden="true">→</span>
            <div className="merge__side">
              <span className="merge__label t-label">{t.to}</span>
              <Select
                label={t.targetLabel}
                options={options}
                value={targetId}
                onChange={setTargetId}
                open={listOpen}
                onOpenChange={setListOpen}
                allowEmpty={false}
                emptyLabel={t.targetEmpty}
              />
              {target ? (
                <p className="merge__hint t-meta">
                  {countOf(target.usageCount ?? 0, forms.brand)}
                </p>
              ) : null}
            </div>
          </div>

          <ConsequenceLine>
            {target ? t.explain(source.name, target.name, sourceBrands) : t.explainNoTarget}
          </ConsequenceLine>
        </div>
      ) : null}
    </Modal>
  )
}
